import { useEffect, useState } from "react";
import { FileText, Loader2, Save, ScrollText, ShieldQuestion, HelpCircle } from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { toast } from "sonner";

const DOCS = [
  { key: "terms", label: "Terms of Service", icon: ScrollText },
  { key: "privacy", label: "Privacy Policy", icon: ShieldQuestion },
  { key: "faq", label: "FAQ", icon: HelpCircle },
];

export function ContentPanel() {
  const [active, setActive] = useState("terms");
  const [body, setBody] = useState("");
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    adminApi.get(`/content/${active}`)
      .then((r) => { if (!ignore) { setBody(r.data.body || ""); setOriginal(r.data.body || ""); } })
      .catch(() => { if (!ignore) { setBody(""); setOriginal(""); } })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [active]);

  const dirty = body !== original;

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/content/${active}`, { body });
      setOriginal(body);
      toast.success(`${DOCS.find((d) => d.key === active)?.label} saved`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save content");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm" data-testid="content-management-panel">
      <div className="mb-4 flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#2340b5]/8"><FileText className="h-5 w-5 text-[#2340b5]" /></span>
        <div>
          <h2 className="font-display text-lg font-bold text-[#12142b]">Content Management</h2>
          <p className="text-sm text-[#565b73]">Edit the legal and help content shown across the SafeRide apps.</p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="content-doc-tabs">
        {DOCS.map((d) => {
          const on = active === d.key;
          return (
            <button
              key={d.key} data-testid={`content-doc-${d.key}`} onClick={() => setActive(d.key)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${on ? "border-[#2340b5] bg-[#2340b5] text-white" : "border-[#12142b]/12 bg-white text-[#12142b] hover:bg-[#f4f6fb]"}`}
            >
              <d.icon className="h-4 w-4" /> {d.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-[#565b73]"><Loader2 className="h-4 w-4 animate-spin" /> Loading content…</div>
      ) : (
        <>
          <textarea
            data-testid="content-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={18}
            className="w-full resize-y rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] p-4 font-mono text-sm leading-relaxed outline-none focus:border-[#2340b5]"
            placeholder="Content body…"
          />
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-[#565b73]">{body.length.toLocaleString()} characters{dirty && " · unsaved changes"}</p>
            <button
              data-testid="content-save-btn" disabled={saving || !dirty} onClick={save}
              className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
            </button>
          </div>
        </>
      )}
    </div>
  );
}
